import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, App, Button, Card, Checkbox, Divider, Form, Input, Spin, Typography } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, apiFetch } from "../api/client";
import { useServerNotifications } from "../api/notifications";
import type {
  NotificationsResponse,
  RoleContactSettingsResponse,
  RoleContactSettingsValues,
} from "../api/types";

/**
 * Two sidebar entries, one screen — the settings of a role that has no data of
 * its own beyond how its holder wants to be reached:
 *
 * - "webmaster_settings"   → /webmaster/webmaster_settings
 * - "zone_coord_settings"  → /circumscription/zone_coordinator_settings
 *
 * Both endpoints answer the same shape and accept the same fields, so the scope
 * only decides which path is called and what the card is called. The speaker
 * and the local coordinator have settings of their own on top of this and get
 * their own screens.
 *
 * The contact data belongs to the *user*, not to the role: changing it here
 * changes it for every role the same person holds. That is the server's model,
 * and the hint under "contact person" is where it becomes visible.
 */
export function RoleContactSettingsPage({
  scope,
}: {
  scope: "webmaster" | "zone_coordinator";
}) {
  const { t } = useTranslation();
  const notify = useServerNotifications();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<RoleContactSettingsValues>();

  const path =
    scope === "webmaster" ? "/webmaster/settings" : "/circumscription/zone_coordinator_settings";
  const queryKey = ["role-settings", scope];

  const settings = useQuery({
    queryKey,
    queryFn: () => apiFetch<RoleContactSettingsResponse>(path),
  });

  useEffect(() => {
    if (!settings.data) return;

    const { role, user } = settings.data.data;
    form.setFieldsValue({
      role_events: role.role_events,
      contact_person: role.contact_person,
      phone: user.phone ?? "",
      mobile: user.mobile ?? "",
      private_email: user.private_email ?? "",
    });
  }, [settings.data, form]);

  const update = useMutation({
    mutationFn: (values: RoleContactSettingsValues) =>
      apiFetch<NotificationsResponse>(path, { method: "PUT", body: values }),
    onSuccess: (data) => {
      notify(data.notifications);
      void queryClient.invalidateQueries({ queryKey });
      // The contact screens list exactly the roles flagged as contact person,
      // so that flag decides what other people see there.
      void queryClient.invalidateQueries({ queryKey: ["circumscription", "contacts"] });
      void queryClient.invalidateQueries({ queryKey: ["congregation", "contacts"] });
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError && error.notifications) {
        notify(error.notifications);
        return;
      }
      void message.error(error instanceof Error ? error.message : String(error));
    },
  });

  if (settings.isPending) return <Spin />;

  if (settings.error) {
    return (
      <Alert
        type="error"
        showIcon
        title={
          settings.error instanceof Error ? settings.error.message : String(settings.error)
        }
      />
    );
  }

  const observer = settings.data?.data.role.observer ?? false;

  return (
    <Card
      title={
        scope === "webmaster"
          ? t("navigation.webmaster_settings")
          : t("navigation.zone_coord_settings")
      }
      style={{ maxWidth: 720 }}
    >
      <Form<RoleContactSettingsValues>
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={(values) => update.mutate(values)}
      >
        <Divider titlePlacement="start">
          {t("role.headers.address")}
        </Divider>

        <Form.Item name="phone" label={t("activerecord.attributes.role.phone")}>
          <Input />
        </Form.Item>

        <Form.Item name="mobile" label={t("activerecord.attributes.role.mobile")}>
          <Input />
        </Form.Item>

        <Form.Item
          name="private_email"
          label={t("activerecord.attributes.role.private_email")}
          rules={[{ type: "email" }]}
        >
          <Input />
        </Form.Item>

        <Divider titlePlacement="start">
          {t("role.headers.settings")}
        </Divider>

        {/* Read-only in the old view too: whether the role may write is granted
            on the role management screen, not chosen by its holder. */}
        <Form.Item>
          <Checkbox checked={observer} disabled>
            {t("activerecord.attributes.role.observer")}
          </Checkbox>
        </Form.Item>

        <Form.Item
          name="role_events"
          valuePropName="checked"
          extra={<Typography.Text type="secondary">{t("role.hints.role_events")}</Typography.Text>}
        >
          <Checkbox>{t("activerecord.attributes.role.role_events")}</Checkbox>
        </Form.Item>

        <Form.Item
          name="contact_person"
          valuePropName="checked"
          extra={
            <Typography.Text type="secondary">{t("role.hints.contact_person")}</Typography.Text>
          }
        >
          <Checkbox>{t("activerecord.attributes.role.contact_person")}</Checkbox>
        </Form.Item>

        <Button type="primary" htmlType="submit" loading={update.isPending}>
          {t("form.update")}
        </Button>
      </Form>
    </Card>
  );
}
